import React, { useState, useEffect } from 'react';
import {
  Users,
  Link as LinkIcon,
  BarChart3,
  Activity,
  Shield,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const StatCard = ({ title, value, icon: Icon, subtitle, accent = 'brand' }) => {
  const accents = {
    brand: 'from-brand-500 to-violet-600',
    emerald: 'from-emerald-500 to-teal-600',
    sky: 'from-sky-500 to-indigo-600',
    rose: 'from-rose-500 to-pink-600',
  };
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-soft">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-ink-500">
            {title}
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-ink-900">
            {Number(value).toLocaleString()}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-ink-500">{subtitle}</p>
          )}
        </div>
        <span
          className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accents[accent]} text-white shadow-soft`}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
};

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-ink-100 last:border-0">
    <span className="text-sm text-ink-600">{label}</span>
    <span className="text-sm font-semibold text-ink-900">
      {Number(value || 0).toLocaleString()}
    </span>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const { loading, error, executeApiCall } = useApi();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const result = await executeApiCall(() => adminAPI.getDashboard());
        if (result.success) setStats(result.data);
      } catch {}
    };
    fetchStats();
  }, [executeApiCall]);

  if (loading) return <LoadingSpinner text="Loading dashboard…" />;
  if (error) return <Alert type="error" message={error} />;
  if (!stats) return <Alert type="info" message="No data available" />;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
          Dashboard
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          A live snapshot of users, links and traffic across the platform.
        </p>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          title="Total users"
          value={stats.users?.total_users || 0}
          icon={Users}
          accent="brand"
          subtitle={`${stats.users?.active_users || 0} active`}
        />
        <StatCard
          title="Total links"
          value={stats.links?.total_links || 0}
          icon={LinkIcon}
          accent="emerald"
          subtitle={`${stats.links?.active_links || 0} active`}
        />
        <StatCard
          title="Total clicks"
          value={stats.analytics?.total_clicks || 0}
          icon={BarChart3}
          accent="sky"
          subtitle={`${stats.analytics?.unique_visitors || 0} unique visitors`}
        />
        <StatCard
          title="Admins"
          value={stats.users?.admin_users || 0}
          icon={Shield}
          accent="rose"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
          <h3 className="text-sm font-semibold text-ink-900">
            User & link activity
          </h3>
          <div className="mt-4">
            <StatRow
              label="New users this week"
              value={stats.users?.new_users_week}
            />
            <StatRow
              label="New users this month"
              value={stats.users?.new_users_month}
            />
            <StatRow
              label="New links this week"
              value={stats.links?.new_links_week}
            />
            <StatRow
              label="New links this month"
              value={stats.links?.new_links_month}
            />
          </div>
        </section>

        <section className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
          <h3 className="text-sm font-semibold text-ink-900">
            Traffic overview
          </h3>
          <div className="mt-4">
            <StatRow
              label="Clicks this week"
              value={stats.analytics?.clicks_week}
            />
            <StatRow
              label="Clicks this month"
              value={stats.analytics?.clicks_month}
            />
            <StatRow
              label="Unique visitors"
              value={stats.analytics?.unique_visitors}
            />
            <StatRow
              label="Total clicks"
              value={stats.analytics?.total_clicks}
            />
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-900 to-ink-800 text-white p-6 shadow-soft">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold tracking-widest text-brand-300 uppercase">
              System
            </p>
            <h3 className="mt-1 text-lg font-semibold tracking-tight">
              All systems operational
            </h3>
            <p className="mt-1 text-sm text-ink-300">
              No incidents in the last 24 hours.
            </p>
          </div>
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-300">
            <Activity className="h-5 w-5" />
          </span>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
